import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {pullRequestsConversationsRoute} from './conversations-route'
import {SplitPageLayout} from '@primer/react'
import {RailsPartial} from '@github-ui/rails-partial'
import styles from './Conversations.module.css'
import {getCurrentUserLogin, isLoggedIn} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {MergeBoxWithSuspense, MergeMethodContextProvider, validateMergeMethod} from '@github-ui/mergebox'
import {clsx} from 'clsx'
import {ConversationSidebarSkeleton} from '../components/ConversationSidebarSkeleton'
import {MergeMethod} from '@github-ui/mergebox/types'
import {usePreheatIssues} from '../hooks/use-preheat-issues'
import {useIsDockedPanelCompact, useIsDockedPanelOpen} from '../contexts/DockedPanelContext'

export function ConversationsEntrypoint() {
  const viewerLogin = getCurrentUserLogin()
  usePreheatIssues()
  const updatedHeader = isFeatureEnabled('updated_pull_request_header')

  const {
    data: {aliveChannel, locked, mergeboxChannels, helpUrl, markAsReadChannel, id, defaultMergeMethod},
  } = useRouteQuery(pullRequestsConversationsRoute, 'mainQuery')

  const mergeMethod = validateMergeMethod(defaultMergeMethod) ? defaultMergeMethod : MergeMethod.MERGE

  const sidePanelOpen = useIsDockedPanelOpen()
  const isCompact = useIsDockedPanelCompact()

  return (
    <div
      className={clsx(
        `js-socket-channel js-updatable-content width-full ${styles.layout}`,
        updatedHeader && styles.contained,
      )}
      data-channel={aliveChannel}
      data-side-panel-open={sidePanelOpen && !isCompact}
    >
      <SplitPageLayout className={styles.wrapper}>
        <SplitPageLayout.Content as="div" padding="none">
          <h2 className="sr-only">Conversation</h2>
          <div
            className="pull-discussion-timeline js-pull-discussion-timeline js-quote-selection-container js-review-state-classes"
            data-quote-markdown=".js-comment-body"
            data-discussion-hovercards-enabled
            data-issue-and-pr-hovercards-enabled
            data-team-hovercards-enabled
            data-hpc
          >
            <div
              className={clsx('js-discussion ml-0 pl-0 tmp-ml-md-6 tmp-pl-md-3', isLoggedIn() && 'js-socket-channel')}
              {...(isLoggedIn()
                ? {
                    'data-channel': markAsReadChannel,
                    'data-channel-target': id,
                  }
                : {})}
            >
              <RailsPartial partialName="Body" route={pullRequestsConversationsRoute} />
              <RailsPartial partialName="Timeline" route={pullRequestsConversationsRoute} />
            </div>
            {!locked && viewerLogin && (
              <MergeMethodContextProvider defaultMergeMethod={mergeMethod}>
                <div className="tmp-ml-md-6 tmp-pl-md-3 tmp-my-3" data-testid="mergebox-partial">
                  <MergeBoxWithSuspense channels={mergeboxChannels} helpUrl={helpUrl} viewerLogin={viewerLogin} />
                </div>
              </MergeMethodContextProvider>
            )}
            <RailsPartial partialName="TimelineActions" route={pullRequestsConversationsRoute} />
          </div>
        </SplitPageLayout.Content>
        <SplitPageLayout.Pane
          className={styles.pane}
          position="end"
          sticky={false}
          divider="none"
          padding="none"
          width="large"
          id="pr-conversation-sidebar"
        >
          <RailsPartial
            partialName="Sidebar"
            route={pullRequestsConversationsRoute}
            skeleton={<ConversationSidebarSkeleton />}
          />
        </SplitPageLayout.Pane>
      </SplitPageLayout>
      {/* Needed for rails and not worth the effort of making them partials */}
      <div hidden>
        <span className="js-add-to-batch-enabled">
          Add this suggestion to a batch that can be applied as a single commit.
        </span>
        <span className="js-unchanged-suggestion">
          This suggestion is invalid because no changes were made to the code.
        </span>
        <span className="js-closed-pull">Suggestions cannot be applied while the pull request is closed.</span>
        <span className="js-viewing-subset-changes">
          Suggestions cannot be applied while viewing a subset of changes.
        </span>
        <span className="js-one-suggestion-per-line">Only one suggestion per line can be applied in a batch.</span>
        <span className="js-reenable-add-to-batch">
          Add this suggestion to a batch that can be applied as a single commit.
        </span>
        <span className="js-validation-on-left-blob">Applying suggestions on deleted lines is not supported.</span>
        <span className="js-validation-on-right-blob">
          You must change the existing code in this line in order to create a valid suggestion.
        </span>
        <span className="js-outdated-comment">Outdated suggestions cannot be applied.</span>
        <span className="js-resolved-thread">This suggestion has been applied or marked resolved.</span>
        <span className="js-pending-review">Suggestions cannot be applied from pending reviews.</span>
        <span className="js-is-multiline">Suggestions cannot be applied on multi-line comments.</span>
        <span className="js-in-merge-queue">
          Suggestions cannot be applied while the pull request is queued to merge.
        </span>
        <span className="js-head-oid-not-loaded">Suggestion cannot be applied right now. Please check back later.</span>
        <div
          className="form-group errored m-0 error js-suggested-changes-inline-validation-template d-flex"
          style={{cursor: 'default'}}
        >
          <span
            className="js-suggested-changes-inline-error-message position-relative error m-0"
            style={{maxWidth: 'inherit'}}
          />
        </div>
      </div>
    </div>
  )
}
